import { useState, useCallback, useMemo, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useConfig, usePrecos, type PrecoTabela } from '@/hooks/useDB';
import { type Cliente, type Cotacao, type Material } from '@/db';
import { freteDoCliente, tipoFrete, type TipoFrete } from '@/utils/frete';
import { saveClienteFrete } from '@/services/clienteFrete';
import { sendCotacaoEmail } from '@/services/quoteEmail';
import { saveCotacao } from '@/services/cotacoesStore';

export type QuoteEmailStatus = { state: 'idle' | 'sending' } | { state: 'sent' | 'error'; message: string };

export interface CalculatorItem {
  materialCodigo: string;
  materialDescricao: string;
  unidadeMedida: string;
  codigoOrigem: string;
  icmsRs: number;
  ipiOriginal: number;
  custoReposicaoBrl: number;
  custoReposicao: number;
  icms: number;
  ipi: number;
  fator: number;
  precoNetMinimo: number;
  precoComImpostosMinimo: number;
  precoPisCofinsSemIcmsMinimo: number;
  precoNegociado: number;
  precoNegociadoComImpostos: number;
  precoPisCofinsSemIcmsNegociado: number;
  margemRealizada: number;
  precoUnitarioUsd: number;
  estoque: number | null;
  precoTabelaNet: number | null;
  moedaTabela: string | null;
  margemTabela: number | null;
  // Peso em kg informado pelo vendedor para este item (vai para a cotação salva, o PDF e o e-mail).
  peso: number | null;
}

type Moeda = 'BRL' | 'USD' | 'EUR';
type PriceLookup = Map<string, PrecoTabela>;

function normalizeCode(value?: string) {
  const normalized = (value || '').trim().toUpperCase().replace(/\s+/g, '');
  return /^\d+$/.test(normalized) ? normalized.replace(/^0+(?=\d)/, '') : normalized;
}

function normalizeDescription(value?: string) {
  return (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase().replace(/[^A-Z0-9]+/g, ' ').trim().replace(/\s+/g, ' ');
}

function priceDate(value?: string) {
  const time = Date.parse(value || '');
  return Number.isNaN(time) ? 0 : time;
}

function buildPriceLookup(precos: PrecoTabela[]): PriceLookup {
  const lookup: PriceLookup = new Map();
  const register = (key: string, price: PrecoTabela) => {
    if (!key) return;
    const current = lookup.get(key);
    if (!current || priceDate(price.ultimaModificacao) > priceDate(current.ultimaModificacao)
      || (priceDate(price.ultimaModificacao) === priceDate(current.ultimaModificacao) && current.drawback && !price.drawback)) {
      lookup.set(key, price);
    }
  };
  for (const price of precos) {
    const client = normalizeCode(price.clienteCodigo);
    register(`${client}|CODE:${normalizeCode(price.materialCodigo)}`, price);
    register(`${client}|NAME:${normalizeDescription(price.produto)}`, price);
  }
  return lookup;
}

const PIS_COFINS = 0.0925;
const FATOR_POR_ICMS: Record<number, number> = {
  0: 0.9075,
  0.04: 0.8712,
  0.07: 0.844,
  0.12: 0.7986,
  0.18: 0.7442,
};
const UFS_12 = new Set(['RS', 'SC', 'SP', 'MG', 'PR', 'RJ']);
const ORIGENS_4 = new Set(['1', '2', '3', '8']);

const round = (value: number, digits = 2) => {
  const power = 10 ** digits;
  return Math.round((value + Number.EPSILON) * power) / power;
};

function exchangeRate(moeda: Moeda, usd: number, eur: number) {
  return moeda === 'USD' ? usd : moeda === 'EUR' ? eur : 1;
}

function aliquotaIcms(material: Material, cliente: Cliente | null, isento: boolean) {
  if (isento || cliente?.uf === 'EX') return 0;
  if (cliente?.uf === 'RS') return material.icmsRs || 0.12;
  if (ORIGENS_4.has(material.codigoOrigem)) return 0.04;
  return UFS_12.has(cliente?.uf || '') ? 0.12 : 0.07;
}

function calcularItem(
  material: Material,
  cliente: Cliente | null,
  precoNegociadoInput: number | null,
  isentoIcms: boolean,
  isentoIpi: boolean,
  isentoPisCofins: boolean,
  moeda: Moeda,
  cotacaoUsd: number,
  cotacaoEur: number,
  priceLookup: PriceLookup,
): CalculatorItem {
  const cambio = exchangeRate(moeda, cotacaoUsd, cotacaoEur) || 1;
  const custo = material.custoReposicao / cambio;
  const icms = aliquotaIcms(material, cliente, isentoIcms);
  const pisCofins = isentoPisCofins ? 0 : PIS_COFINS;
  const fator = isentoPisCofins ? 1 - icms : (FATOR_POR_ICMS[icms] ?? 1 - icms - PIS_COFINS);
  const ipi = isentoIpi ? 0 : material.ipi / 100;

  const precoNetMinimo = custo / 0.6;
  const precoComImpostosMinimo = precoNetMinimo / fator;
  const precoPisCofinsSemIcmsMinimo = precoNetMinimo / (fator + pisCofins);
  const precoNegociado = precoNegociadoInput ?? precoNetMinimo;
  const precoNegociadoComImpostos = precoNegociado / fator;
  const precoPisCofinsSemIcmsNegociado = precoNegociado / (fator + pisCofins);
  const margemRealizada = precoNegociado > 0 ? 1 - custo / precoNegociado : 0;
  const precoUnitarioUsd = (precoNegociado * cambio) / (cotacaoUsd || 1);

  const clientCode = cliente?.codigo || '';
  const normalizedClient = normalizeCode(clientCode);
  const tabela = priceLookup.get(`${normalizedClient}|CODE:${normalizeCode(material.codigo)}`)
    ?? priceLookup.get(`${normalizedClient}|NAME:${normalizeDescription(material.descricao)}`);
  const moedaTabela = tabela?.moeda === 'USD' || tabela?.moeda === 'EUR' ? tabela.moeda : 'BRL';
  const custoNaMoedaTabela = material.custoReposicao / exchangeRate(moedaTabela, cotacaoUsd, cotacaoEur);
  const margemTabela = tabela?.precoNet ? 1 - custoNaMoedaTabela / tabela.precoNet : null;

  return {
    materialCodigo: material.codigo,
    materialDescricao: material.descricao,
    unidadeMedida: material.unidadeMedida || 'KG',
    codigoOrigem: material.codigoOrigem,
    icmsRs: material.icmsRs,
    ipiOriginal: material.ipi,
    custoReposicaoBrl: material.custoReposicao,
    custoReposicao: round(custo, 5),
    icms,
    ipi,
    fator,
    precoNetMinimo: round(precoNetMinimo, 5),
    precoComImpostosMinimo: round(precoComImpostosMinimo, 5),
    precoPisCofinsSemIcmsMinimo: round(precoPisCofinsSemIcmsMinimo, 5),
    precoNegociado: round(precoNegociado, 5),
    precoNegociadoComImpostos: round(precoNegociadoComImpostos, 5),
    precoPisCofinsSemIcmsNegociado: round(precoPisCofinsSemIcmsNegociado, 5),
    margemRealizada: round(margemRealizada * 100, 2),
    precoUnitarioUsd: round(precoUnitarioUsd, 5),
    estoque: material.estoque,
    precoTabelaNet: tabela ? round(tabela.precoNet, 5) : null,
    moedaTabela: tabela?.moeda ?? null,
    margemTabela: margemTabela == null ? null : round(margemTabela * 100, 2),
    peso: null,
  };
}

export function useCalculadora() {
  const { user } = useAuth();
  const { config } = useConfig();
  const { precos } = usePrecos();
  const priceLookup = useMemo(() => buildPriceLookup(precos), [precos]);
  const [clienteSelecionado, setClienteSelecionadoState] = useState<Cliente | null>(null);
  const [itens, setItens] = useState<CalculatorItem[]>([]);
  const [moeda, setMoeda] = useState<Moeda>('BRL');
  const [cotacaoUsd, setCotacaoUsd] = useState(5.1183);
  const [cotacaoEur, setCotacaoEur] = useState(5.8364);
  const [margemAlvo, setMargemAlvo] = useState(40);
  const [isentoIpi, setIsentoIpi] = useState(false);
  const [isentoIcms, setIsentoIcms] = useState(false);
  const [isentoPisCofins, setIsentoPisCofins] = useState(false);
  const [condicaoPagamento, setCondicaoPagamento] = useState('');
  const [frete, setFrete] = useState<TipoFrete | ''>('');
  const [salvarFreteNoCadastro, setSalvarFreteNoCadastro] = useState(true);
  const [freteCadastroErro, setFreteCadastroErro] = useState('');
  const [saved, setSaved] = useState(false);
  const [emailStatus, setEmailStatus] = useState<QuoteEmailStatus>({ state: 'idle' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (config) {
      if (config.cotacaoUsd) setCotacaoUsd(config.cotacaoUsd);
      if (config.cotacaoEur) setCotacaoEur(config.cotacaoEur);
      if (config.margemAlvo) setMargemAlvo(config.margemAlvo);
    }
  }, [config]);

  const materialFromItem = useCallback((item: CalculatorItem): Material => ({
    id: 0,
    codigo: item.materialCodigo,
    descricao: item.materialDescricao,
    origemFiscal: '',
    custoReposicao: item.custoReposicaoBrl,
    ipi: item.ipiOriginal,
    icmsRs: item.icmsRs,
    codigoOrigem: item.codigoOrigem,
    estoque: item.estoque,
    unidadeMedida: item.unidadeMedida,
  }), []);

  const recalcAll = useCallback((current: CalculatorItem[], keepNegotiated = true) => current.map((item) => ({
    ...calcularItem(materialFromItem(item), clienteSelecionado, keepNegotiated ? item.precoNegociado : null,
      isentoIcms, isentoIpi, isentoPisCofins, moeda, cotacaoUsd, cotacaoEur, priceLookup),
    peso: item.peso,
  })),
  [clienteSelecionado, isentoIcms, isentoIpi, isentoPisCofins, moeda, cotacaoUsd, cotacaoEur, materialFromItem, priceLookup]);

  const setClienteSelecionado = useCallback((cliente: Cliente | null) => {
    setClienteSelecionadoState(cliente);
    setCondicaoPagamento(cliente?.condicaoPagamento?.trim() || 'A combinar');
    setFrete(freteDoCliente(cliente) ?? '');
    setSalvarFreteNoCadastro(true);
    setFreteCadastroErro('');
    setSaved(false);
  }, []);

  const addItem = useCallback((material: Material) => {
    setItens((prev) => [...prev, calcularItem(material, clienteSelecionado, null, isentoIcms,
      isentoIpi, isentoPisCofins, moeda, cotacaoUsd, cotacaoEur, priceLookup)]);
    setSaved(false);
  }, [clienteSelecionado, isentoIcms, isentoIpi, isentoPisCofins, moeda, cotacaoUsd, cotacaoEur, priceLookup]);

  const removeItem = useCallback((index: number) => setItens((prev) => prev.filter((_, i) => i !== index)), []);
  const updatePrecoNegociado = useCallback((index: number, value: number) => {
    setItens((prev) => prev.map((item, i) => i === index
      ? { ...calcularItem(materialFromItem(item), clienteSelecionado, value, isentoIcms, isentoIpi,
        isentoPisCofins, moeda, cotacaoUsd, cotacaoEur, priceLookup), peso: item.peso }
      : item));
    setSaved(false);
  }, [clienteSelecionado, isentoIcms, isentoIpi, isentoPisCofins, moeda, cotacaoUsd, cotacaoEur, materialFromItem, priceLookup]);

  // Peso em kg do item (vazio ou 0 = não informado).
  const updatePeso = useCallback((index: number, value: number | null) => {
    setItens((prev) => prev.map((item, i) => i === index ? { ...item, peso: value } : item));
    setSaved(false);
  }, []);

  useEffect(() => {
    if (itens.length) setItens((prev) => recalcAll(prev));
  // Recalcula também quando a Tabela CIGAM termina de carregar ou é atualizada.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recalcAll]);

  const totals = useMemo(() => {
    const totalSemImpostos = itens.reduce((sum, item) => sum + item.precoNegociado, 0);
    const total = itens.reduce((sum, item) => sum + item.precoNegociadoComImpostos, 0);
    const totalBrl = total * exchangeRate(moeda, cotacaoUsd, cotacaoEur);
    const margemMedia = itens.length ? itens.reduce((sum, item) => sum + item.margemRealizada, 0) / itens.length : 0;
    return { totalSemImpostos: round(totalSemImpostos), total: round(total), totalBrl: round(totalBrl), margemMedia: round(margemMedia, 1) };
  }, [itens, moeda, cotacaoUsd, cotacaoEur]);

  const salvarCotacao = useCallback(async () => {
    if (!clienteSelecionado || !itens.length || !frete) return;
    setSaving(true);
    try {
      const cotacao: Omit<Cotacao, 'id'> = { clienteCodigo: clienteSelecionado.codigo, clienteNome: clienteSelecionado.nome,
        vendedor: user?.name || 'Vendedor', divisao: clienteSelecionado.divisao, moeda, itens: itens.map((item) => ({
          materialCodigo: item.materialCodigo, materialDescricao: item.materialDescricao,
          unidadeMedida: item.unidadeMedida, custoReposicao: item.custoReposicao, icms: item.icms,
          ipi: item.ipi, fator: item.fator, precoNetMinimo: item.precoNetMinimo,
          precoComImpostosMinimo: item.precoComImpostosMinimo, precoNegociado: item.precoNegociado,
          precoNegociadoComImpostos: item.precoNegociadoComImpostos,
          margemRealizada: item.margemRealizada, precoUnitarioConvertido: item.precoUnitarioUsd,
          ...(item.peso ? { peso: item.peso } : {}),
        })), total: totals.total, totalBrl: totals.totalBrl, margemMedia: totals.margemMedia,
        beneficioFiscal: isentoIcms || isentoIpi || isentoPisCofins, isentoIpi, isentoIcms,
        isentoPisCofins, condicaoPagamento: clienteSelecionado.condicaoPagamento?.trim() || 'A combinar',
        frete, cotacaoUsd, cotacaoEur, margemAlvo,
        createdAt: new Date().toISOString() };
      const { cotacao: salva, remote } = await saveCotacao(cotacao);
      if (!remote) console.warn('Cotação guardada só neste navegador; será enviada à nuvem na próxima abertura.');
      // Sem frete no CIGAM: guarda a escolha no cadastro do cliente para as próximas cotações.
      if (!tipoFrete(clienteSelecionado.frete) && salvarFreteNoCadastro && frete !== tipoFrete(clienteSelecionado.freteManual)) {
        const codigo = clienteSelecionado.codigo;
        void saveClienteFrete(codigo, frete).then((erro) => {
          setFreteCadastroErro(erro ?? '');
          if (!erro) setClienteSelecionadoState((atual) => atual?.codigo === codigo ? { ...atual, freteManual: frete } : atual);
        });
      }
      setSaved(true);
      setEmailStatus({ state: 'sending' });
      const cliente = clienteSelecionado;
      void sendCotacaoEmail(salva, cliente).then((result) => setEmailStatus(!('error' in result)
        ? { state: 'sent', message: result.recipient } : { state: 'error', message: result.error }));
    } finally { setSaving(false); }
  }, [clienteSelecionado, itens, frete, salvarFreteNoCadastro, user, moeda, totals, isentoIcms, isentoIpi, isentoPisCofins,
    cotacaoUsd, cotacaoEur, margemAlvo]);

  const novaCotacao = useCallback(() => {
    setClienteSelecionadoState(null); setItens([]); setSaved(false); setIsentoIcms(false);
    setIsentoIpi(false); setIsentoPisCofins(false); setCondicaoPagamento(''); setFrete(''); setMoeda('BRL');
    setEmailStatus({ state: 'idle' });
  }, []);

  return { clienteSelecionado, itens, moeda, cotacaoUsd, cotacaoEur, margemAlvo,
    isentoIpi, isentoIcms, isentoPisCofins, condicaoPagamento, frete, setFrete, salvarFreteNoCadastro, setSalvarFreteNoCadastro, freteCadastroErro, saved, saving, emailStatus, totals,
    setClienteSelecionado, setMoeda, setCotacaoUsd, setCotacaoEur, setMargemAlvo,
    setIsentoIpi, setIsentoIcms, setIsentoPisCofins, setCondicaoPagamento, addItem,
    removeItem, updatePrecoNegociado, updatePeso, salvarCotacao, novaCotacao };
}
